import { batch } from 'react-redux';
import { message } from 'antd';

import api from '@/api';

const ABORT_ERROR_CODE = 20;
let kyController = new AbortController();

const getWellStates = wells => {
    if (!wells?.length) return {};

    const result = wells.reduce((acc, well) => {
        const { wellId, gtmDates } = well;
        const hasGtm = gtmDates.length > 0;
        const state = hasGtm;
        return { ...acc, [wellId]: state };
    }, {});
    return result;
};

const addCalcForIndicators = indicators => {
    if (!indicators) return;
    const extendedIndicators = { ...indicators };
    Object.keys(extendedIndicators).forEach(indicatorKey => {
        if (indicatorKey === 'oilDebit') {
            extendedIndicators[indicatorKey].calc = ({ oilValue, daysProd }) => oilValue / daysProd || 0;
        }
        if (indicatorKey === 'liquidDebit') {
            extendedIndicators[indicatorKey].calc = ({ waterValue, daysProd }) => waterValue / daysProd || 0;
        }
        if (indicatorKey === 'watering') {
            extendedIndicators[indicatorKey].calc = ({ oilValue, waterValue }) =>
                (1 - oilValue / waterValue) * 100 || 0;
        }
        if (indicatorKey === 'levelDinamic') {
            extendedIndicators[indicatorKey].calc = ({ levelDinamic, wellCountProd }) =>
                levelDinamic / wellCountProd || 0;
        }
        if (indicatorKey === 'reception') {
            extendedIndicators[indicatorKey].calc = ({ injection, daysInj }) => injection / daysInj || 0;
        }
    });
    return extendedIndicators;
};

const consolidatedProductionModel = {
    name: 'consolidatedProduction',
    state: {
        data: null,
        wells: null,
        wellStates: null,
        indicators: null,

        activities: null,
        isSettingsVisible: true,
        activitiesType: 'gtm',
        selectedActivity: 6,
        firstPeriod: null,
        secondPeriod: null,
        periodActivitiesType: 'first',
        stepsBefore: 5,
        stepsAfter: 5,

        selectedWell: null,
        loading: false,
    },
    reducers: {
        /* consolidated production */
        setData(state, payload) {
            return {
                ...state,
                data: payload,
            };
        },
        setWells(state, payload) {
            return {
                ...state,
                wells: payload,
            };
        },
        setWellStates(state, payload) {
            return {
                ...state,
                wellStates: payload,
            };
        },
        setIndicators(state, payload) {
            return {
                ...state,
                indicators: payload,
            };
        },
        setActivities(state, payload) {
            return {
                ...state,
                activities: payload,
            };
        },
        setIsSettingsVisible(state, payload) {
            return {
                ...state,
                isSettingsVisible: payload,
            };
        },
        setActivitiesType(state, payload) {
            return {
                ...state,
                activitiesType: payload,
                selectedSetting: null,
            };
        },
        setSelectedActivity(state, payload) {
            return {
                ...state,
                selectedActivity: payload,
            };
        },
        setFirstPeriod(state, payload) {
            return {
                ...state,
                firstPeriod: payload,
            };
        },
        setSecondPeriod(state, payload) {
            return {
                ...state,
                secondPeriod: payload,
            };
        },
        setPeriodActivitiesType(state, payload) {
            return {
                ...state,
                periodActivitiesType: payload,
            };
        },
        setStepsBefore(state, payload) {
            return {
                ...state,
                stepsBefore: payload,
            };
        },
        setStepsAfter(state, payload) {
            return {
                ...state,
                stepsAfter: payload,
            };
        },
        setSelectedWell(state, payload) {
            return {
                ...state,
                selectedWell: payload,
            };
        },

        setLoading(state, payload) {
            return {
                ...state,
                loading: payload,
            };
        },
    },

    effects: () => ({
        cancelRequest() {
            kyController.abort();
            kyController = new AbortController();

            this.setLoading(false);
        },

        async fetchData(payload) {
            this.setLoading(true);
            try {
                const consolidatedProductionData = await api.miningAndPumping.fetchConsolidatedProductionData({
                    json: payload,
                    signal: kyController.signal,
                });
                if (Object.keys(consolidatedProductionData) < 1) {
                    message.info('Недостаточно данных для построения графика в режиме "Сведённая добыча"');
                } else {
                    const { wells, data, indicators } = consolidatedProductionData;
                    const wellsList = Object.keys(wells).map(wellKey => wells[wellKey]);
                    const wellStates = getWellStates(wellsList, payload.gtmId);
                    const extendedIndicators = addCalcForIndicators(indicators);
                    this.setData(data);
                    this.setWells(wells);
                    this.setWellStates(wellStates);
                    this.setIndicators(extendedIndicators);
                }
            } catch (error) {
                console.error(error);
                batch(() => {
                    this.setData(null);
                    this.setWells(null);
                    this.setWellStates(null);
                    this.setIndicators(null);
                });
                if (error.code === ABORT_ERROR_CODE) return;

                message.error('Не удалось получить данные для режима "Сведённая добыча"');
            }
            this.setLoading(false);
        },

        async fetchSettings(payload) {
            try {
                const data = await api.miningAndPumping.fetchConsolidatedProductionSettings(payload);
                const gtm = data?.gtm.map(({ id, title }) => ({ value: id, label: title }));
                const actionGroup = data?.actionGroup.map(({ id, title }) => ({ value: id, label: title }));
                this.setActivities({ gtm, actionGroup });
            } catch (error) {
                console.error(error);
                message.error('Не удалось получить настройки для режима "Сведённая добыча"');
            }
        },
    }),
};

export default consolidatedProductionModel;
